import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BuildingOffice2Icon, BeakerIcon } from '@heroicons/react/24/outline';
import { alertApi } from '../../lib/api';

/**
 * Commercial/SB + Ag/SF volume & distribution breakdown (Bobby, 2026-06-04). The "louder"
 * companion to the consumer rate-disparity results: isolates the Commercial and Agricultural
 * loans the RC-C classifier tagged, and shows the CRA small-business (≤ $1M) and small-farm
 * (≤ $500K) threshold splits — counts + Original-Amount $ volume — that feed the CRA tables.
 */

interface Segment {
  key: string;
  label: string;
  count: number;
  amount: number;
}
interface SegmentPair {
  small: Segment;
  over: Segment;
  total: { count: number; amount: number };
  smallShareOfCount: number | null;
  smallShareOfAmount: number | null;
}
interface YearDistribution {
  year: string;
  totals: { count: number; amount: number };
  commercial: SegmentPair;
  agricultural: SegmentPair;
}
interface Distribution {
  scope: 'all' | 'inPeriod';
  totals: { count: number; amount: number };
  segments: Segment[];
  commercial: SegmentPair;
  agricultural: SegmentPair;
  byYear: YearDistribution[];
}

const usd = (n: number) =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
      ? `$${(n / 1_000).toFixed(0)}K`
      : `$${n.toFixed(0)}`;
const usdFull = (n: number) => `$${Math.round(n).toLocaleString()}`;
const pct = (f: number | null) => (f == null ? '—' : `${(f * 100).toFixed(1)}%`);

function FamilyCard({
  title,
  icon,
  pair,
  smallLabel,
  overLabel,
  accent,
}: {
  title: string;
  icon: React.ReactNode;
  pair: SegmentPair;
  smallLabel: string;
  overLabel: string;
  accent: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className={accent}>{icon}</span>
        <h3 className="text-base font-bold text-navy-900">{title}</h3>
        <span className="ml-auto text-xs text-gray-400">
          {pair.total.count.toLocaleString()} loans · {usd(pair.total.amount)}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className={`rounded-lg ${accent.replace('text-', 'bg-').replace('-600', '-50')} p-3`}>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{smallLabel}</p>
          <p className="mt-1 text-2xl font-bold text-navy-900">{pair.small.count.toLocaleString()}</p>
          <p className="text-sm text-gray-600">{usdFull(pair.small.amount)}</p>
          <p className="mt-1 text-xs text-gray-400">{pct(pair.smallShareOfCount)} of family by count</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{overLabel}</p>
          <p className="mt-1 text-2xl font-bold text-navy-900">{pair.over.count.toLocaleString()}</p>
          <p className="text-sm text-gray-600">{usdFull(pair.over.amount)}</p>
          <p className="mt-1 text-xs text-gray-400">over threshold</p>
        </div>
      </div>
    </div>
  );
}

export default function AlertLoanDistributionSection({ fileId }: { fileId: string }) {
  const [scope, setScope] = useState<'all' | 'inPeriod'>('all');
  const { data, isLoading } = useQuery({
    queryKey: ['alert-distribution', fileId, scope],
    queryFn: async () => (await alertApi.getLoanDistribution(fileId, scope)).data as { distribution: Distribution },
    enabled: !!fileId,
  });
  const d = data?.distribution;

  if (isLoading) {
    return <div className="rounded-xl border border-gray-100 p-6 text-sm text-gray-400">Loading distribution…</div>;
  }
  if (!d || d.totals.count === 0) return null;

  const { commercial, agricultural, totals } = d;

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-2xl font-bold text-navy-900">Commercial &amp; Agricultural Distribution</h2>
        <div className="inline-flex rounded-lg border border-gray-200 text-xs overflow-hidden">
          <button
            onClick={() => setScope('all')}
            className={`px-3 py-1.5 font-medium ${scope === 'all' ? 'bg-ocean-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            Whole file
          </button>
          <button
            onClick={() => setScope('inPeriod')}
            className={`px-3 py-1.5 font-medium ${scope === 'inPeriod' ? 'bg-ocean-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            In analysis period
          </button>
        </div>
      </div>

      {/* Headline family cards — the loud part */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <FamilyCard
          title="Commercial / C&I"
          icon={<BuildingOffice2Icon className="h-5 w-5" />}
          accent="text-ocean-600"
          pair={commercial}
          smallLabel="Small Business · ≤ $1M"
          overLabel="Commercial · > $1M"
        />
        <FamilyCard
          title="Agricultural"
          icon={<BeakerIcon className="h-5 w-5" />}
          accent="text-emerald-600"
          pair={agricultural}
          smallLabel="Small Farm · ≤ $500K"
          overLabel="Large Ag · > $500K"
        />
      </div>

      {/* Full segment breakdown */}
      <details className="rounded-lg border border-gray-100">
        <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 select-none">
          Full loan-type breakdown ({totals.count.toLocaleString()} loans · {usdFull(totals.amount)})
        </summary>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Segment</th>
              <th className="px-4 py-2 text-right font-medium">Loans</th>
              <th className="px-4 py-2 text-right font-medium">% of count</th>
              <th className="px-4 py-2 text-right font-medium">Original $ volume</th>
              <th className="px-4 py-2 text-right font-medium">% of $</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {d.segments
              .filter((s) => s.count > 0)
              .map((s) => (
                <tr key={s.key}>
                  <td className="px-4 py-2 text-gray-700">{s.label}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{s.count.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                    {pct(totals.count > 0 ? s.count / totals.count : null)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{usdFull(s.amount)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                    {pct(totals.amount > 0 ? s.amount / totals.amount : null)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </details>

      {/* Itemized by origination year — aggregate above is primary; this is the dropdown for
          multi-year files (e.g. 2024–YTD 2026). */}
      {d.byYear.length > 0 && (
        <details className="mt-3 rounded-lg border border-gray-100">
          <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 select-none">
            Itemized by year ({d.byYear.length} year{d.byYear.length === 1 ? '' : 's'} with origination dates)
          </summary>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Year</th>
                <th className="px-4 py-2 text-right font-medium">Small Business (≤$1M)</th>
                <th className="px-4 py-2 text-right font-medium">Commercial (&gt;$1M)</th>
                <th className="px-4 py-2 text-right font-medium">Small Farm (≤$500K)</th>
                <th className="px-4 py-2 text-right font-medium">Large Ag (&gt;$500K)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {d.byYear.map((y) => {
                const cell = (s: Segment) => (
                  <td className="px-4 py-2 text-right tabular-nums">
                    {s.count.toLocaleString()}
                    <span className="ml-1 text-xs text-gray-400">· {usd(s.amount)}</span>
                  </td>
                );
                return (
                  <tr key={y.year}>
                    <td className="px-4 py-2 font-medium text-gray-700">{y.year}</td>
                    {cell(y.commercial.small)}
                    {cell(y.commercial.over)}
                    {cell(y.agricultural.small)}
                    {cell(y.agricultural.over)}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="px-4 py-2 text-[11px] text-gray-400">
            By origination year (Original Amount). Loans without an origination date are in the aggregate but not itemized here.
          </p>
        </details>
      )}

      <p className="mt-2 text-xs text-gray-400">
        Segments derive from the RC-C call-report code: Small Business = 1E/4/1A2 ≤ $1M, Small Farm = 1B/3 ≤ $500K
        (1A1 routes to Residential). $ volume is Original Amount.
      </p>
    </section>
  );
}
